'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { CalculatorValues, getDefaultCurrency } from './types';
import { ToolsTranslations } from '@/lib/translations/types';
import { useToolTracking } from '@/lib/analytics';

// Components
import CalculatorCard from './CalculatorCard';
import SavingsChart from './SavingsChart';
import SavingsTable from './SavingsTable';
import RiskAssessment from './RiskAssessment';
import { calculateCompoundInterest, generateChartData, getRiskLevel, getRiskColors } from './calculations';
import { createTranslatedAssetAllocationData } from './types';
import { DividerText } from '@/components/utils/Divider';

interface Props {
  translations: ToolsTranslations['savings'];
}

export default function SavingsCalculator({ translations }: Props) {
  const defaultCurrency = getDefaultCurrency();
  
  const [values, setValues] = useState<CalculatorValues>({
    monthlySavings: 1200,
    startingAmount: 15000,
    savingsPeriod: 15,
    expectedReturn: 7,
    currency: defaultCurrency // Use the correct currency from the start
  });

  // Initialize tracking
  const { trackUsage } = useToolTracking('savings');
  
  // Track if we've already sent a usage event for this session
  const hasTrackedUsage = useRef(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Only override with localStorage if user has a saved preference that's different from locale default
  useEffect(() => {
    const savedCurrency = localStorage.getItem('currency');
    const localeDefaultCurrency = getDefaultCurrency();
    
    // Only use localStorage if it contains a currency different from the locale default
    // This respects the user's explicit choice while using locale-appropriate defaults
    if (savedCurrency && savedCurrency !== localeDefaultCurrency) {
      setValues(prev => ({ ...prev, currency: savedCurrency }));
    }
  }, [values.currency]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Calculate results using utility functions
  const { totalSavings, finalAmount, totalReturn } = calculateCompoundInterest(values);
  const chartData = generateChartData(values, translations);
  const riskInfo = getRiskLevel(values.expectedReturn, translations);
  const riskColors = getRiskColors(riskInfo);
  
  // Create translated asset allocation data
  const translatedAssetData = createTranslatedAssetAllocationData(translations);

  // Debounced tracking function - only track once per session after user stops interacting
  const debouncedTrackUsage = useCallback(() => {
    // Clear any existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // Only track if we haven't already tracked usage in this session
    if (!hasTrackedUsage.current) {
      debounceTimeout.current = setTimeout(() => {
        trackUsage();
        hasTrackedUsage.current = true;
      }, 2000); // Wait 2 seconds after last interaction
    }
  }, [trackUsage]);

  // Handle value changes
  const handleValuesChange = (field: keyof CalculatorValues, value: number | string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Track tool usage with debouncing - only once per session
    debouncedTrackUsage();
  };

  return (
    <div id="savings-calculator" className="text-center space-y-10 relative tools">
      <div className="space-y-4">
        <h2 className="text-3xl lg:text-4xl font-bold text-black">{translations.title}</h2>
        <p className="text-lg text-black/80 max-w-3xl mx-auto leading-relaxed">{translations.subtitle}</p>
      </div>

      {/* Calculator and Chart Grid */}
      <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
        {/* Calculator Card */}
        <CalculatorCard
          values={values}
          onValuesChange={handleValuesChange}
          finalAmount={finalAmount}
          totalReturn={totalReturn}
          translations={translations}
        />

        {/* Chart Card */}
        <SavingsChart
          chartData={chartData}
          totalSavings={totalSavings}
          totalReturn={totalReturn}
          riskInfo={riskInfo}
          riskColors={riskColors}
          translations={translations}
          currency={values.currency}
        />
      </div>

      {/* Table View */}
      <SavingsTable
        chartData={chartData}
        translations={translations}
        currency={values.currency}
      />

      {/* Divider */}
      {/* <DividerText message={translations.dividerMessage} showArrows={true} /> */}

      {/* Risk Assessment Section */}
      {/* <RiskAssessment 
        riskInfo={riskInfo} 
        riskColors={riskColors} 
        translations={translations}
        assetLabels={translatedAssetData.labels}
      /> */}

      {/* Disclaimer */}
      <div className="text-sm text-black/80 max-w-3xl mx-auto">{translations.disclaimerText}</div>
    </div>
  );
}
